/**
 * The views you drew recently, so Home can offer them back.
 *
 * A recent view is stored as the same query string the share link and the
 * catalog use - `buildQueryString()` in, `restoreFromUrl()` out. Nothing else
 * would be honest: any second description of "what was on screen" drifts from
 * the one the rest of the app restores from.
 *
 * It lives in `localStorage`, per browser, and is written after a plot has
 * already succeeded. That makes Home useful with no database, and it makes the
 * strip free to paint - no request, no chart re-run, just what is stored.
 *
 * The picture is a small JPEG of the chart that was on screen. Storage is the
 * constraint, so entries are capped and a quota error drops the pictures rather
 * than the history: a recent view without a preview is still a way back in.
 */

import { Atom } from './atom.ts';
import { captureThumbnailAt } from '../features/catalog/thumbnail.ts';

export const RECENTS_KEY = 'neptune_recents_v1';

/** How many are kept. Home shows four; the rest are the "see all" overflow. */
export const MAX_RECENTS = 8;

const THUMB_WIDTH = 240;
const THUMB_HEIGHT = 135;
const THUMB_QUALITY = 0.6;

export interface RecentView {
  /** Query string, no leading `?` - restored through `restoreFromUrl`. */
  q: string;
  /** Ship ids the view drew, for the label and for grouping. */
  ships: string[];
  plotType: string;
  /** Epoch ms of the last time this view was drawn. */
  ts: number;
  /** Small JPEG data URL, when the chart could be captured. */
  thumb?: string;
}

function isRecent(value: unknown): value is RecentView {
  const r = value as RecentView;
  return (
    !!r &&
    typeof r.q === 'string' &&
    typeof r.plotType === 'string' &&
    typeof r.ts === 'number' &&
    Array.isArray(r.ships)
  );
}

function read(): RecentView[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecent).slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function write(list: RecentView[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {
    // Out of quota - almost always the pictures. Keep the history, lose the
    // previews, and only give up if even that will not fit.
    try {
      localStorage.setItem(
        RECENTS_KEY,
        JSON.stringify(list.map(({ thumb: _thumb, ...rest }) => rest)),
      );
    } catch {
      /* Storage is unavailable; the strip is empty next load. */
    }
  }
}

export const recentViews = new Atom<RecentView[]>(read());

/**
 * What makes two views "the same view" for the strip.
 *
 * Deliberately not the whole query string. Nudging a date range or a filter is
 * still the same investigation, and keying on the exact URL would fill all
 * eight slots with one afternoon's variations of a single chart. Ship, plot
 * type and the columns being drawn is what someone recognises.
 */
export function recentKey(view: Pick<RecentView, 'q' | 'ships' | 'plotType'>): string {
  const params = new URLSearchParams(view.q);
  const series = ['yl', 'yr', 'hc', 'x', 'hy', 'hz', 'lat', 'lon']
    .map((key) => params.get(key) ?? '')
    .join('|');
  return `${view.plotType}::${[...view.ships].sort().join(',')}::${series}`;
}

/**
 * Record the view that was just drawn.
 *
 * Called after a successful plot, never before: a recent view that fails to
 * open is worse than no recent view. Re-drawing something already in the list
 * moves it to the front and refreshes its picture instead of adding a
 * near-duplicate.
 */
export function recordRecent(input: { q: string; ships: string[]; plotType: string }): void {
  if (!input.q) return;
  const entry: RecentView = {
    q: input.q,
    ships: input.ships,
    plotType: input.plotType,
    ts: Date.now(),
  };
  const thumb = captureThumbnailAt(THUMB_WIDTH, THUMB_HEIGHT, 'image/jpeg', THUMB_QUALITY);
  if (thumb) entry.thumb = thumb;

  const key = recentKey(entry);
  const rest = recentViews.value.filter((r) => recentKey(r) !== key);
  const next = [entry, ...rest].slice(0, MAX_RECENTS);
  recentViews.set(next);
  write(next);
}

export function removeRecent(q: string): void {
  const next = recentViews.value.filter((r) => r.q !== q);
  recentViews.set(next);
  write(next);
}

export function clearRecents(): void {
  recentViews.set([]);
  write([]);
}
